import { useEffect, useState } from "react";
import { Branch } from "../../../models/BranchModels";
import "./BranchModal.css";

export type BranchModalMode = "create" | "edit";

interface BranchModalProps {
  isOpen: boolean;
  mode: BranchModalMode;
  branch: Branch | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (form: BranchFormValues) => Promise<void>;
}

export interface BranchFormValues {
  name: string;
  phone: string;
  email: string;
  description: string;
  address: string;
  city: string;
  area: string;
  latitude: string;
  longitude: string;
  isActive: boolean;
}

const emptyForm: BranchFormValues = {
  name: "",
  phone: "",
  email: "",
  description: "",
  address: "",
  city: "",
  area: "",
  latitude: "",
  longitude: "",
  isActive: true,
};

const fromBranch = (branch: Branch): BranchFormValues => ({
  name: branch.name,
  phone: branch.phone || "",
  email: branch.email || "",
  description: branch.description || "",
  address: branch.address || "",
  city: branch.city || "",
  area: branch.area || "",
  latitude:
    branch.latitude !== null && branch.latitude !== undefined
      ? String(branch.latitude)
      : "",
  longitude:
    branch.longitude !== null && branch.longitude !== undefined
      ? String(branch.longitude)
      : "",
  isActive: branch.isActive,
});

type TextKey = Exclude<keyof BranchFormValues, "isActive">;

const BranchModal = ({
  isOpen,
  mode,
  branch,
  submitting,
  onClose,
  onSubmit,
}: BranchModalProps) => {
  const [form, setForm] = useState<BranchFormValues>(emptyForm);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === "create") {
      setForm(emptyForm);
    } else if (branch) {
      setForm(fromBranch(branch));
    }
  }, [isOpen, mode, branch]);

  if (!isOpen) return null;

  const setField = (key: TextKey, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  const textField = (
    key: TextKey,
    label: string,
    opts?: { type?: string; placeholder?: string; full?: boolean },
  ) => (
    <div
      className={`branch-form-group ${opts?.full ? "branch-form-group--full" : ""}`}
    >
      <label htmlFor={`branch-${key}`}>{label}</label>
      <input
        id={`branch-${key}`}
        type={opts?.type ?? "text"}
        value={form[key]}
        placeholder={opts?.placeholder}
        onChange={(e) => setField(key, e.target.value)}
      />
    </div>
  );

  return (
    <div className="branch-modal-overlay">
      <div className="branch-modal" onClick={(e) => e.stopPropagation()}>
        <div className="branch-modal__header">
          <h3>{mode === "create" ? "Add Branch" : "Edit Branch"}</h3>
          <button
            className="branch-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="branch-modal__body" onSubmit={handleSubmit}>
          <div className="branch-form-grid">
            <div className="branch-form-group branch-form-group--full">
              <label htmlFor="branch-name">Name *</label>
              <input
                id="branch-name"
                type="text"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
              />
            </div>

            {textField("phone", "Phone", { type: "tel" })}
            {textField("email", "Email", { type: "email" })}
            {textField("city", "City")}
            {textField("area", "Area")}
            {textField("address", "Address", { full: true })}
            {textField("latitude", "Latitude", { type: "number" })}
            {textField("longitude", "Longitude", { type: "number" })}
          </div>

          <div className="branch-form-group branch-form-group--full">
            <label htmlFor="branch-description">Description</label>
            <textarea
              id="branch-description"
              rows={3}
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
            />
          </div>

          <div className="branch-form-group branch-form-group--full">
            <label className="branch-form-check">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, isActive: e.target.checked }))
                }
              />
              Active
            </label>
          </div>

          <div className="branch-modal__actions">
            <button
              type="button"
              className="branch-btn branch-btn--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="branch-btn branch-btn--primary"
              disabled={submitting || !form.name.trim()}
            >
              {submitting
                ? "Saving..."
                : mode === "create"
                  ? "Add Branch"
                  : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BranchModal;